/**
 * Control Centre — Daily Driver + Maintenance Checks PDF for the selected period
 * (defaults to yesterday).
 * Layout mirrors Profitability Report: black header/footer bands, ZeeFleet logo, tables.
 */

import { isPlaceholderOutstandingDefectRow } from './defectRules';
import { jsPDF } from 'jspdf';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './apiClient';
import { fetchStoredViewJson } from './storedView';
import { formatDriverDisplayName, formatDriverExamplesSummary } from './formatDriverDisplayName';
import { checkedCountOf, formatWalkaroundTimeHm, isCheckedStatus, shiftStartOf, type WalkaroundCheckStatus } from './walkaroundCheckRows';
import type { DailySafetyBriefResponse } from '../components/fleetDataHub/DailySafetyBriefPanel';

/** Sidebar logo (dark-friendly) for black header/footer bands. */
const LOGO_PATH = 'images/landing/logo/zeefleet-v2-logo-negative-tight.png';
/** Intrinsic aspect of zeefleet-v2-logo-negative-tight.png */
export const LOGO_ASPECT = 1224 / 122;

export interface ControlCentreDailyChecksPdfOptions {
  /** Driver system label, e.g. Aquarius */
  driverSourceLabel?: string;
  /** Selected window (ISO). Falls back to yesterday when absent or unusable. */
  from?: string;
  to?: string;
}

interface CheckComplianceRow {
  day?: string;
  driver_key: string;
  driver_name: string;
  vehicle: string;
  /** When the shift began (clocking on) — not the first drive. */
  shift_start_at?: string | null;
  /** Legacy name for shift_start_at; see utils/walkaroundCheckRows. */
  first_drive_at?: string | null;
  check_started_at: string | null;
  check_ended_at?: string | null;
  /** Real instants of the three times above (zeecore applies the Aquarius mapping). */
  shift_start_instant?: string | null;
  check_started_instant?: string | null;
  check_ended_instant?: string | null;
  check_duration_minutes?: number | null;
  short_check?: boolean;
  defect_count: number;
  status: WalkaroundCheckStatus | string;
}

interface CheckComplianceResponse {
  kpis: {
    drivers_who_drove: number;
    missing: number;
    no_drive: number;
    checked?: number;
    short_check?: number;
    pct_checked?: number;
    /** Legacy; summed only as a fallback when `checked` is absent. */
    before_drive?: number;
    after_drive?: number;
  };
  rows: CheckComplianceRow[];
}

type WalkaroundStatusTone = 'red' | 'amber' | 'green' | 'gray';
type PdfRgb = [number, number, number];

interface WalkaroundKpiCard {
  label: string;
  value: string;
  accent: PdfRgb;
}

const WALKAROUND_STATUS_COLORS: Record<
  WalkaroundStatusTone,
  { background: PdfRgb; border: PdfRgb; text: PdfRgb }
> = {
  red: { background: [254, 226, 226], border: [248, 113, 113], text: [185, 28, 28] },
  amber: { background: [254, 243, 199], border: [251, 191, 36], text: [180, 83, 9] },
  green: { background: [220, 252, 231], border: [74, 222, 128], text: [21, 128, 61] },
  gray: { background: [241, 245, 249], border: [203, 213, 225], text: [71, 85, 105] },
};

export function buildWalkaroundKpiCards(
  kpis: CheckComplianceResponse['kpis'] | undefined,
  rows: Pick<CheckComplianceRow, 'short_check' | 'defect_count'>[],
): WalkaroundKpiCard[] {
  const driversWhoDrove = Number(kpis?.drivers_who_drove || 0);
  const checked = checkedCountOf(kpis);
  const pctCheck =
    typeof kpis?.pct_checked === 'number'
      ? kpis.pct_checked
      : driversWhoDrove <= 0
        ? 0
        : Math.round((checked / driversWhoDrove) * 1000) / 10;
  const pctAccent: PdfRgb =
    pctCheck >= 90 ? [22, 163, 74] : pctCheck >= 70 ? [217, 119, 6] : [220, 38, 38];

  return [
    { label: 'Missing check', value: String(Number(kpis?.missing || 0)), accent: [220, 38, 38] },
    {
      label: 'Checked under 15 min',
      value: String(
        typeof kpis?.short_check === 'number'
          ? kpis.short_check
          : rows.filter((row) => row.short_check).length,
      ),
      accent: [217, 119, 6],
    },
    {
      label: 'Defects found',
      value: String(rows.filter((row) => Number(row.defect_count ?? 0) > 0).length),
      accent: [139, 92, 246],
    },
    { label: '% check', value: `${pctCheck}%`, accent: pctAccent },
  ];
}

export function walkaroundStatusDisplay(row: {
  status: WalkaroundCheckStatus | string;
  short_check?: boolean;
}): { label: string; tone: WalkaroundStatusTone } {
  if (row.status === 'missing') return { label: 'Missing', tone: 'red' };
  if (row.short_check && isCheckedStatus(row.status)) {
    return { label: 'Checked under 15 min', tone: 'amber' };
  }
  if (isCheckedStatus(row.status)) return { label: 'Checked', tone: 'green' };
  if (row.status === 'unverified') return { label: 'Not verified', tone: 'gray' };
  return { label: 'No drive', tone: 'gray' };
}

export function orderWalkaroundRows<T extends {
  status: WalkaroundCheckStatus | string;
  short_check?: boolean;
}>(rows: T[]): T[] {
  const priority = (row: T) => {
    if (row.status === 'missing') return 0;
    if (row.short_check && isCheckedStatus(row.status)) return 1;
    return 2;
  };
  return rows
    .map((row, index) => ({ row, index, priority: priority(row) }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .map(({ row }) => row);
}

interface MaintenanceSummaryForPdf {
  defects: Array<{
    vehicle: string;
    details: string;
    reported_date: string;
    date?: string;
    status?: string;
    portal_id?: string;
    resolved?: boolean;
    vor_state?: boolean;
    latest_audit?: { datetime?: string } | null;
  }>;
  vor_assets: Array<{
    vehicle: string;
    reason: string;
    vor_category: string;
    vor_days: number | null;
    vor_start: string;
    date_reported: string;
  }>;
}

export function sanitizePdfText(raw: string, maxLen?: number): string {
  let t = String(raw ?? '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim();
  t = t.replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-');
  t = t.replace(/\u2026/g, '...');
  t = t.replace(/\u2192|\u2794|\u279D/g, '->');
  t = t.replace(/[\u00B7\u2022\u2023\u2219]/g, '|');
  if (maxLen && t.length > maxLen) t = `${t.slice(0, Math.max(0, maxLen - 3))}...`;
  return t || '-';
}

export async function loadControlCentreLogoForPdf(): Promise<string | null> {
  try {
    const base = import.meta.env.BASE_URL.endsWith('/')
      ? import.meta.env.BASE_URL
      : `${import.meta.env.BASE_URL}/`;
    const res = await fetch(`${base}${LOGO_PATH}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('logo image load failed'));
        img.src = objUrl;
      });
      const w = img.naturalWidth || 1224;
      const h = img.naturalHeight || 122;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  } catch {
    return null;
  }
}

interface PdfRange {
  from: string;
  to: string;
  /** Filename fragment: a single day, or "from_to_to". */
  slug: string;
  label: string;
}

function utcYmd(value: Date | number): string {
  return new Date(value).toISOString().slice(0, 10);
}

function yesterdayUtcRange(): PdfRange {
  const y = new Date();
  y.setUTCDate(y.getUTCDate() - 1);
  const ymd = utcYmd(y);
  return {
    from: `${ymd}T00:00:00.000Z`,
    to: `${ymd}T23:59:59.999Z`,
    slug: ymd,
    label: `Yesterday (${ymd})`,
  };
}

/** Mirror the tab's selected window so the report matches what the user is looking at. */
function resolvePdfRange(from?: string, to?: string): PdfRange {
  if (!from || !to) return yesterdayUtcRange();
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) {
    return yesterdayUtcRange();
  }

  const fromYmd = utcYmd(fromMs);
  const toYmd = utcYmd(toMs);
  if (fromYmd !== toYmd) {
    return { from, to, slug: `${fromYmd}_to_${toYmd}`, label: `${fromYmd} to ${toYmd}` };
  }

  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const named =
    fromYmd === utcYmd(new Date()) ? 'Today' : fromYmd === utcYmd(yesterday) ? 'Yesterday' : null;
  return {
    from,
    to,
    slug: fromYmd,
    label: named ? `${named} (${fromYmd})` : fromYmd,
  };
}

/** Same rule as the Driver tab (utils/walkaroundCheckRows), with the PDF's "-" placeholder. */
function formatTimeHm(instant: string | null | undefined, stored: string | null | undefined): string {
  return formatWalkaroundTimeHm(instant, stored, '-');
}

function cleanDefectDetails(details: string | null | undefined): string {
  return String(details || '')
    .replace(/\s*\(Aquarius assets summary\)\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isFixedDefectRow(d: {
  status?: string;
  resolved?: boolean;
}): boolean {
  const status = String(d.status || '').trim().toLowerCase().replace(/[\s_-]/g, '');
  return status === 'fixed' || d.resolved === true;
}

function formatDefectStatusLabel(status: string | null | undefined, resolved?: boolean): string {
  const raw = String(status || '').trim() || (resolved ? 'Fixed' : 'Notfixed');
  const key = raw.toLowerCase().replace(/[\s_-]/g, '');
  const labels: Record<string, string> = {
    notfixed: 'Not Fixed',
    inprogress: 'In Progress',
    fixed: 'Fixed',
    nextpmi: 'Next PMI',
  };
  return labels[key] || raw;
}

function formatDateYmd(value: string | null | undefined): string {
  if (!value) return '-';
  const m = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toISOString().slice(0, 10);
}

function daysAgoSortKey(value: string | null | undefined): number {
  if (!value) return -1;
  const ms = Date.parse(String(value));
  if (Number.isNaN(ms)) return -1;
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const reported = new Date(ms);
  const reportedUtc = Date.UTC(reported.getUTCFullYear(), reported.getUTCMonth(), reported.getUTCDate());
  return Math.max(0, Math.floor((todayUtc - reportedUtc) / 86_400_000));
}

function formatDaysAgo(value: string | null | undefined): string {
  const days = daysAgoSortKey(value);
  if (days < 0) return '-';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/** Dashboard: green <7d · amber 7–20d · red ≥21d */
function daysAgoToneRgb(days: number): [number, number, number] | null {
  if (days < 0) return null;
  if (days < 7) return [22, 163, 74];
  if (days < 21) return [217, 119, 6];
  return [220, 38, 38];
}

/** Dashboard defect status: Not Fixed red · In Progress / Next PMI amber · Fixed green */
function defectStatusToneRgb(label: string): [number, number, number] | null {
  const key = String(label || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '');
  if (key === 'notfixed') return [220, 38, 38];
  if (key === 'inprogress' || key === 'nextpmi') return [217, 119, 6];
  if (key === 'fixed') return [22, 163, 74];
  return null;
}

function formatWindowPhrase(fromIso?: string | null, toIso?: string | null): string {
  const fromYmd = String(fromIso || '').slice(0, 10);
  const toYmd = String(toIso || '').slice(0, 10);
  if (fromYmd && toYmd && fromYmd !== toYmd) return `from ${fromYmd} to ${toYmd}`;
  if (fromYmd) return `on ${fromYmd}`;
  return 'for the selected window';
}

/** Strip "Examples:" / "Vehicles:" prefixes; format driver names or vehicle regs for PDF. */
export function formatBriefSummaryForPdf(
  summary: string | null | undefined,
  kind: 'drivers' | 'vehicles',
  preserveLines = false,
): string {
  if (preserveLines) {
    return String(summary || '')
      .split(/\r?\n/)
      .map((line) => sanitizePdfText(line))
      .filter(Boolean)
      .join('\n');
  }
  return sanitizePdfText(formatDriverExamplesSummary(summary, kind));
}

export function formatBriefHeadlineForPdf(
  headline: string,
  today = utcYmd(new Date()),
): string {
  return headline.replace(/\s+on\s+(\d{4}-\d{2}-\d{2})\s*$/i, (match, date: string) =>
    date === today ? ' (Today)' : match,
  );
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    // Driver tab periods are stored views: a 202 "preparing" is waited out, not printed.
    return await fetchStoredViewJson<T>(() => authenticatedFetch(url));
  } catch {
    return null;
  }
}

export async function buildControlCentreDailyChecksPdf(
  opts?: ControlCentreDailyChecksPdfOptions,
): Promise<{ blob: Blob; filename: string } | null> {
  const range = resolvePdfRange(opts?.from, opts?.to);
  const driverSource = (opts?.driverSourceLabel || 'Aquarius').trim() || 'Aquarius';

  const [logoPng, driverBrief, maintBrief, checks, maintenance] = await Promise.all([
    loadControlCentreLogoForPdf(),
    fetchJson<DailySafetyBriefResponse>(
      API_ENDPOINTS.fleetIntelligence.dailySafetyBrief({
        from: range.from,
        to: range.to,
        scope: 'driver',
      }),
    ),
    fetchJson<DailySafetyBriefResponse>(
      API_ENDPOINTS.fleetIntelligence.dailySafetyBrief({
        from: range.from,
        to: range.to,
        scope: 'maintenance',
      }),
    ),
    fetchJson<CheckComplianceResponse>(
      API_ENDPOINTS.fleetIntelligence.driversCheckCompliance({
        from: range.from,
        to: range.to,
      }),
    ),
    fetchJson<MaintenanceSummaryForPdf>(
      API_ENDPOINTS.fleetIntelligence.maintenanceSummary({
        from: range.from,
        to: range.to,
      }),
    ),
  ]);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const innerW = pageW - margin * 2;
  const rightX = pageW - margin;
  const footerBandH = 14;
  const footerRow1Y = pageH - 9;
  const footerRow2Y = pageH - 4.8;
  const contentBottom = pageH - footerBandH - 6;

  let pageCount = 1;

  const footer = (pageNum: number, totalPages: number) => {
    doc.setFillColor(0, 0, 0);
    doc.rect(0, pageH - footerBandH, pageW, footerBandH, 'F');
    doc.setDrawColor(55, 55, 55);
    doc.setLineWidth(0.25);
    doc.line(0, pageH - footerBandH, pageW, pageH - footerBandH);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(230, 230, 230);
    doc.text(`Page ${pageNum} / ${totalPages}`, rightX, footerRow1Y, { align: 'right' });
    if (logoPng) {
      const fh = 3.6;
      const fw = fh * LOGO_ASPECT;
      doc.addImage(logoPng, 'PNG', margin, footerRow1Y - fh + 0.9, fw, fh);
      doc.text('Daily checks summary', margin + fw + 2, footerRow1Y);
    } else {
      doc.text('Daily checks summary', margin, footerRow1Y);
    }
    doc.setFontSize(7);
    const visitPrefix = 'For more, visit: ';
    const visitUrl = 'https://zeefleet.co.uk';
    doc.setFont('helvetica', 'normal');
    const prefixW = doc.getTextWidth(visitPrefix);
    const urlW = doc.getTextWidth(visitUrl);
    const linkStartX = pageW / 2 - (prefixW + urlW) / 2;
    doc.setTextColor(200, 200, 200);
    doc.text(visitPrefix, linkStartX, footerRow2Y);
    doc.setTextColor(96, 165, 250);
    doc.textWithLink(visitUrl, linkStartX + prefixW, footerRow2Y, { url: visitUrl });
    doc.setDrawColor(96, 165, 250);
    doc.setLineWidth(0.2);
    doc.line(linkStartX + prefixW, footerRow2Y + 0.55, linkStartX + prefixW + urlW, footerRow2Y + 0.55);
    doc.setTextColor(0, 0, 0);
  };

  const ensureSpace = (y: number, need: number): number => {
    if (y + need <= contentBottom) return y;
    doc.addPage();
    pageCount += 1;
    return 18;
  };

  /** Pill-style source badges (matches Control Centre Source: labels). */
  const drawSourceLabels = (yy: number, sources: string[]): void => {
    const labels = sources.map((s) => sanitizePdfText(s).trim()).filter(Boolean);
    if (!labels.length) return;
    const padX = 2.2;
    const gap = 2.2;
    const h = 5.2;
    doc.setFontSize(6.5);
    const widths = labels.map((name) => {
      doc.setFont('helvetica', 'normal');
      const prefixW = doc.getTextWidth('Source: ');
      doc.setFont('helvetica', 'bold');
      const nameW = doc.getTextWidth(name.toUpperCase());
      return prefixW + nameW + padX * 2;
    });
    const totalW = widths.reduce((s, w) => s + w, 0) + gap * (labels.length - 1);
    let x = rightX - totalW;
    labels.forEach((name, i) => {
      const w = widths[i];
      doc.setFillColor(224, 242, 254); // cyan-100
      doc.setDrawColor(14, 165, 233); // sky-500
      doc.setLineWidth(0.35);
      doc.roundedRect(x, yy - 3.6, w, h, 1, 1, 'FD');
      doc.setTextColor(3, 105, 161); // sky-700
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      const prefix = 'Source: ';
      const prefixW = doc.getTextWidth(prefix);
      doc.text(prefix, x + padX, yy);
      doc.setFont('helvetica', 'bold');
      doc.text(name.toUpperCase(), x + padX + prefixW, yy);
      x += w + gap;
    });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42);
  };

  const sectionHeading = (yy: number, title: string, sources?: string[]): number => {
    yy = ensureSpace(yy, 16);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(sanitizePdfText(title), margin, yy);
    if (sources?.length) drawSourceLabels(yy, sources);
    yy += 5;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.45);
    doc.line(margin, yy, rightX, yy);
    yy += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42);
    return yy;
  };

  const renderBriefBlock = (
    yy: number,
    brief: DailySafetyBriefResponse | null,
    fallbackTitle: string,
    summaryKind: 'drivers' | 'vehicles',
  ): number => {
    yy = ensureSpace(yy, 20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    const windowPhrase = formatWindowPhrase(brief?.date_from || range.from, brief?.date_to || range.to);
    const rawHeadline = String(brief?.headline || fallbackTitle);
    const headlineWithDates = /for the selected window/i.test(rawHeadline)
      ? rawHeadline.replace(/for the selected window/gi, windowPhrase)
      : rawHeadline;
    const headline = sanitizePdfText(formatBriefHeadlineForPdf(headlineWithDates), 180);
    const headLines = doc.splitTextToSize(headline, innerW);
    for (const line of headLines) {
      yy = ensureSpace(yy, 5);
      doc.text(line, margin, yy);
      yy += 4.5;
    }
    yy += 2;

    const items = brief?.items?.length ? brief.items : [];
    if (!items.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text('No brief items for this day.', margin, yy);
      return yy + 6;
    }

    items.forEach((item, idx) => {
      yy = ensureSpace(yy, 12);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(15, 23, 42);
      const title = sanitizePdfText(`${idx + 1}. ${item.title}`, 120);
      doc.text(title, margin, yy);
      yy += 4.2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(71, 85, 105);
      const summaryText = formatBriefSummaryForPdf(
        item.summary,
        summaryKind,
        item.category === 'schedule',
      );
      const summaryLines = summaryText
        .split('\n')
        .flatMap((summaryLine) => doc.splitTextToSize(summaryLine, innerW));
      for (const line of summaryLines.slice(0, 4)) {
        yy = ensureSpace(yy, 4);
        doc.text(line, margin, yy);
        yy += 3.8;
      }
      yy += 2.5;
      doc.setTextColor(15, 23, 42);
    });
    return yy + 2;
  };

  const drawKpiCards = (
    yy: number,
    cards: Array<{ label: string; value: string; accent: [number, number, number] }>,
  ): number => {
    yy = ensureSpace(yy, 22);
    const gap = 3;
    const cardW = (innerW - gap * 3) / 4;
    const cardH = 16;
    cards.forEach((card, i) => {
      const x = margin + i * (cardW + gap);
      doc.setDrawColor(card.accent[0], card.accent[1], card.accent[2]);
      doc.setFillColor(248, 250, 252);
      doc.setLineWidth(0.4);
      doc.roundedRect(x, yy, cardW, cardH, 1.2, 1.2, 'FD');
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(sanitizePdfText(card.label, 28), x + 2.5, yy + 5);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(card.accent[0], card.accent[1], card.accent[2]);
      doc.text(sanitizePdfText(card.value), x + 2.5, yy + 12.5);
    });
    return yy + cardH + 6;
  };

  type Col = { key: string; label: string; width: number; align?: 'left' | 'right' };
  const drawStatusCell = (
    text: string,
    tone: WalkaroundStatusTone,
    x: number,
    yy: number,
    maxW: number,
  ) => {
    const raw = sanitizePdfText(text, 80);
    const colors = WALKAROUND_STATUS_COLORS[tone];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    const pillW = Math.min(maxW - 2, doc.getTextWidth(raw) + 3);
    const pillH = 4.1;
    doc.setFillColor(...colors.background);
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.25);
    doc.roundedRect(x, yy - 3.15, pillW, pillH, 1, 1, 'FD');
    doc.setTextColor(...colors.text);
    const clipped = doc.splitTextToSize(raw, pillW - 2)[0] || '-';
    doc.text(clipped, x + 1, yy - 0.4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 41, 59);
  };

  const drawTable = (
    yy: number,
    cols: Col[],
    rows: Record<string, string>[],
    opts?: {
      statusKey?: string;
      statusToneKey?: string;
      cellColor?: (row: Record<string, string>, key: string) => [number, number, number] | null;
    },
  ): number => {
    const rowH = 5.2;
    const headerH = 6;
    const statusKey = opts?.statusKey;
    const statusToneKey = opts?.statusToneKey;
    const cellColor = opts?.cellColor;

    const drawHeader = (y: number) => {
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, y - 4, innerW, headerH, 'F');
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      let x = margin + 1.5;
      for (const c of cols) {
        if (c.align === 'right') {
          doc.text(sanitizePdfText(c.label), x + c.width - 1.5, y, { align: 'right' });
        } else {
          doc.text(sanitizePdfText(c.label), x, y);
        }
        x += c.width;
      }
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.3);
      doc.line(margin, y + 2, rightX, y + 2);
      return y + headerH - 1;
    };

    yy = ensureSpace(yy, headerH + rowH * 2);
    yy = drawHeader(yy);

    if (!rows.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('No rows for this period.', margin + 1.5, yy + 2);
      return yy + 8;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    rows.forEach((row, idx) => {
      if (yy + rowH > contentBottom) {
        doc.addPage();
        pageCount += 1;
        yy = 18;
        yy = drawHeader(yy);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
      }
      if (idx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, yy - 3.2, innerW, rowH, 'F');
      }
      doc.setTextColor(30, 41, 59);
      let x = margin + 1.5;
      for (const c of cols) {
        const cell = row[c.key] ?? '-';
        const tone = cellColor?.(row, c.key) ?? null;
        if (statusKey && c.key === statusKey) {
          const statusTone = row[statusToneKey ?? ''] as WalkaroundStatusTone | undefined;
          drawStatusCell(cell, statusTone ?? 'gray', x, yy, c.width);
        } else {
          if (tone) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(tone[0], tone[1], tone[2]);
          } else {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 41, 59);
          }
          if (c.align === 'right') {
            doc.text(sanitizePdfText(cell, 80), x + c.width - 1.5, yy, { align: 'right' });
          } else {
            const clipped = doc.splitTextToSize(sanitizePdfText(cell, 80), c.width - 2)[0] || '-';
            doc.text(clipped, x, yy);
          }
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(30, 41, 59);
        }
        x += c.width;
      }
      yy += rowH;
    });
    return yy + 4;
  };

  // ---- Header ----
  doc.setFillColor(0, 0, 0);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  if (logoPng) {
    const lh = 7.2;
    const lw = lh * LOGO_ASPECT;
    doc.addImage(logoPng, 'PNG', rightX - lw, 5.5, lw, lh);
  }
  doc.text('Daily Checks Report', margin, 12);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(230, 230, 230);
  doc.text('Control Centre · Driver & Maintenance snapshot', margin, 18.5);
  doc.setFontSize(8);
  doc.text(`Period: ${sanitizePdfText(range.label)}`, rightX, 18.5, { align: 'right' });
  doc.text(`Generated: ${sanitizePdfText(new Date().toLocaleString('en-GB'))}`, rightX, 23.5, {
    align: 'right',
  });
  doc.setTextColor(0, 0, 0);

  let y = 36;

  // ---- Section 1: Driver ----
  y = sectionHeading(y, '1. Driver Checks Summary', [driverSource]);
  y = renderBriefBlock(y, driverBrief, 'Daily Driver Brief', 'drivers');

  const kpis = checks?.kpis;
  y = drawKpiCards(y, buildWalkaroundKpiCards(kpis, checks?.rows || []));

  y = ensureSpace(y, 10);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Walkaround Check Table', margin, y);
  y += 6;

  const walkCols: Col[] = [
    { key: 'driver', label: 'Driver', width: innerW * 0.16 },
    { key: 'vehicle', label: 'Vehicle', width: innerW * 0.11 },
    { key: 'shift', label: 'Shift start', width: innerW * 0.10 },
    { key: 'checkStart', label: 'Check Start', width: innerW * 0.12 },
    { key: 'checkEnd', label: 'Check End', width: innerW * 0.12 },
    { key: 'duration', label: 'Duration', width: innerW * 0.11 },
    { key: 'status', label: 'Status', width: innerW * 0.18 },
    { key: 'defects', label: 'Defects', width: innerW * 0.10, align: 'right' },
  ];
  const walkRows = orderWalkaroundRows(checks?.rows || []).map((r) => {
    const mins = r.check_duration_minutes;
    const status = walkaroundStatusDisplay(r);
    const durationLabel =
      mins == null
        ? '-'
        : r.short_check
          ? `${mins} min (short)`
          : `${mins} min`;
    return {
      driver: formatDriverDisplayName(r.driver_name) || r.driver_key || '-',
      vehicle: String(r.vehicle || '-').trim().toUpperCase() || '-',
      shift: formatTimeHm(r.shift_start_instant, shiftStartOf(r)),
      checkStart: formatTimeHm(r.check_started_instant, r.check_started_at),
      checkEnd: formatTimeHm(r.check_ended_instant, r.check_ended_at),
      duration: durationLabel,
      status: status.label,
      _statusTone: status.tone,
      defects: String(r.defect_count ?? 0),
      _durationShort: r.short_check ? '1' : '0',
    };
  });
  y = drawTable(y, walkCols, walkRows, {
    statusKey: 'status',
    statusToneKey: '_statusTone',
    cellColor: (row, key) => {
      if (key === 'duration' && row._durationShort === '1') return [217, 119, 6];
    if (key === 'defects' && Number(row.defects) > 0) return [147, 51, 234];
    return null;
    },
  });

  // ---- Section 2: Maintenance ----
  y += 14;
  y = ensureSpace(y, 40);
  y = sectionHeading(y, '2. Maintenance Checks Summary', ['Aquarius', 'Samsara']);
  y = renderBriefBlock(y, maintBrief, 'Daily Maintenance Brief', 'vehicles');

  y = ensureSpace(y, 10);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Defect status', margin, y);
  drawSourceLabels(y, ['Aquarius']);
  y += 7;

  const defectCols: Col[] = [
    { key: 'vehicle', label: 'Vehicle Reg', width: innerW * 0.14 },
    { key: 'updated', label: 'Last Updated', width: innerW * 0.16 },
    { key: 'status', label: 'Status', width: innerW * 0.14 },
    { key: 'details', label: 'Details', width: innerW * 0.44 },
    { key: 'vor', label: 'VOR', width: innerW * 0.12 },
  ];
  const defectRows = (maintenance?.defects || [])
    .filter((d) => !isPlaceholderOutstandingDefectRow(d))
    .filter((d) => !isFixedDefectRow(d))
    .map((d) => {
      const lastUpdatedAt = d.reported_date || d.date || d.latest_audit?.datetime || '';
      return {
        vehicle: String(d.vehicle || '-').trim().toUpperCase() || '-',
        updated: formatDaysAgo(lastUpdatedAt),
        _updatedDays: String(daysAgoSortKey(lastUpdatedAt)),
        status: formatDefectStatusLabel(d.status, d.resolved),
        details: cleanDefectDetails(d.details) || '-',
        vor: d.vor_state ? 'Yes' : 'No',
      };
    });
  y = drawTable(y, defectCols, defectRows, {
    cellColor: (row, key) => {
      if (key === 'updated') return daysAgoToneRgb(Number(row._updatedDays));
      if (key === 'status') return defectStatusToneRgb(row.status);
      return null;
    },
  });

  y = ensureSpace(y, 10);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Vehicles on VOR', margin, y);
  drawSourceLabels(y, ['Aquarius']);
  y += 7;

  const vorCols: Col[] = [
    { key: 'vehicle', label: 'Vehicle Reg', width: innerW * 0.14 },
    { key: 'start', label: 'Start Date', width: innerW * 0.14 },
    { key: 'days', label: 'Days Off-Road', width: innerW * 0.12, align: 'right' },
    { key: 'updated', label: 'Last Updated', width: innerW * 0.14 },
    { key: 'reason', label: 'Reason', width: innerW * 0.28 },
    { key: 'category', label: 'Category', width: innerW * 0.18 },
  ];
  const vorRows = (maintenance?.vor_assets || []).map((v) => ({
    vehicle: String(v.vehicle || '-').trim().toUpperCase() || '-',
    start: formatDateYmd(v.vor_start),
    days: v.vor_days != null ? String(v.vor_days) : '-',
    updated: formatDaysAgo(v.date_reported),
    _updatedDays: String(daysAgoSortKey(v.date_reported)),
    reason: v.reason || '-',
    category: v.vor_category || '-',
  }));
  y = drawTable(y, vorCols, vorRows, {
    cellColor: (row, key) => {
      if (key === 'updated') return daysAgoToneRgb(Number(row._updatedDays));
      return null;
    },
  });

  // Patch page numbers on all pages
  const totalPages = pageCount;
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    footer(p, totalPages);
  }

  const filename = `zecure-daily-checks-${range.slug}.pdf`;
  const blob = doc.output('blob');
  return { blob, filename };
}
